/**
 * Zum Prüfen der Aufbereitung: der Abschnitt als NISO-STS-XML und seine Blöcke als JSON.
 * Kein Werkzeug für Autoren — ein Blick unter die Haube, wenn die Ansicht nicht stimmt.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy } from "lucide-react";

import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { cn } from "../lib/utils";
import { useGuidelinesAPI, type SectionStructure } from "./GuidelinesAPI";

/** XML eingerückt, ein Element je Zeile; Text bleibt auf der Zeile seines Elements. */
export const indentXml = (xml: string): string => {
  const lines: string[] = [];
  let depth = 0;
  let afterText = false;
  for (const token of xml.match(/<[^>]+>|[^<]+/g) ?? []) {
    if (!token.startsWith("<")) {
      if (!token.trim()) continue;
      if (lines.length) lines[lines.length - 1] += token;
      else lines.push(token);
      afterText = true;
    } else if (token.startsWith("</")) {
      depth = Math.max(0, depth - 1);
      if (afterText && lines.length) lines[lines.length - 1] += token;
      else lines.push("  ".repeat(depth) + token);
      afterText = false;
    } else {
      lines.push("  ".repeat(depth) + token);
      if (!token.endsWith("/>") && !token.startsWith("<?") && !token.startsWith("<!")) depth++;
      afterText = false;
    }
  }
  return lines.join("\n");
};

export function StsDialog(props: { customer: string; documentId: string; sectionId: string; heading: string; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const [data, setData] = useState<SectionStructure | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"sts" | "json">("sts");

  useEffect(() => {
    if (!props.open) return;
    setData(null);
    setError(null);
    api.getSectionStructure(props.documentId, props.sectionId).then(setData, (e) => setError(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.documentId, props.sectionId]);

  const shown = data ? (view === "sts" ? indentXml(data.xml) : JSON.stringify({ blocks: data.blocks, footnotes: data.footnotes }, null, 2)) : "";

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col">
        <DialogHeader>
          <DialogTitle>{t("guidelinesPage.sts.title", { heading: props.heading })}</DialogTitle>
          <DialogDescription>
            {data ? (data.hasBlocks ? t("guidelinesPage.sts.withBlocks", { rev: data.rev ?? "–" }) : t("guidelinesPage.sts.noBlocks")) : t("guidelinesPage.sts.loading")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border p-0.5 text-xs" role="group">
            {(["sts", "json"] as const).map((v) => (
              <button key={v} type="button" onClick={() => setView(v)} aria-pressed={view === v} className={cn("rounded px-2 py-1", view === v ? "bg-accent font-medium" : "text-muted-foreground")}>
                {t(`guidelinesPage.sts.${v}`)}
              </button>
            ))}
          </div>
          <Button size="sm" variant="ghost" className="ml-auto" disabled={!shown} onClick={() => navigator.clipboard?.writeText(shown).catch(() => {})}>
            <Copy size={14} className="mr-1" />
            {t("guidelinesPage.sts.copy")}
          </Button>
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <pre className="min-h-0 flex-1 overflow-auto rounded-md border bg-muted/40 p-3 font-mono text-xs leading-5">{shown}</pre>
      </DialogContent>
    </Dialog>
  );
}
